import { useEffect, useRef, useState } from 'react';

// ============================================================================
// CountUp — RAF-driven number ticker that animates between value changes.
//
// Used on dashboard / stat tiles to give a sense of "fresh data just landed"
// rather than the value popping in instantly. Cheap: one rAF loop per mount,
// auto-cancelled on unmount or value change. Respects prefers-reduced-motion
// (skips the animation entirely; value snaps to final).
//
// Props:
//   end       — target value (number)
//   duration  — ms to animate from old → new value (default 800)
//   decimals  — fixed-point digits (default 0)
//   prefix    — string before number (e.g. '$')
//   suffix    — string after number  (e.g. '%')
//   delay     — ms to wait before starting (default 0)
//
// Renders as a plain text node so it slots into any layout.
// ============================================================================

function easeOutCubic(t) {
  return 1 - Math.pow(1 - t, 3);
}

export default function CountUp({
  end,
  duration = 800,
  decimals = 0,
  prefix = '',
  suffix = '',
  delay = 0,
}) {
  const [value, setValue] = useState(end);
  const rafRef = useRef(null);
  const timeoutRef = useRef(null);
  const fromRef = useRef(end);

  useEffect(() => {
    if (typeof end !== 'number' || !Number.isFinite(end)) {
      setValue(end);
      return;
    }

    // Honour reduced-motion preference — snap straight to the new value.
    const reduced = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || duration <= 0) {
      setValue(end);
      return;
    }

    const from = fromRef.current ?? end;
    const startAnim = () => {
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = easeOutCubic(t);
        const next = from + (end - from) * eased;
        setValue(next);
        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          fromRef.current = end;
        }
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    if (delay > 0) {
      timeoutRef.current = setTimeout(startAnim, delay);
    } else {
      startAnim();
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      fromRef.current = end;
    };
  }, [end, duration, delay]);

  const display = (typeof value === 'number' && Number.isFinite(value))
    ? value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : String(value ?? '');

  return <>{prefix}{display}{suffix}</>;
}
